import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import { io } from 'socket.io-client';

import socketServerUrl from 'api/Socket';

import isBinary from 'lib/helpers/isBinary';

const eventRegistry = new Map();
const reconnectEmitRegistry = new Set();
let autoRehydrate = true;
let connectedOnce = false;
let pendingEmitAfterReconnect = false;

const socket = io(socketServerUrl, {
  autoConnect: false,
  withCredentials: true,
  transports: ['websocket', 'polling'],
  reconnection: true
});

socket.startedAuth = false;

/**
 * Registers an event to the current socket, replacing any existing handler,
 * and adds it to the mechanism for re-registering the event in case of reconnect.
 * @param {string} event - Event that you want to listen to, e.g. appointment-open.
 * @param {Function} handler - Callback handler when socket sends us this event.
 */
function socketOn(event, handler) {
  const existingHandler = eventRegistry.get(event);
  if (existingHandler) {
    socket.off(event, existingHandler);
  }

  socket.on(event, handler);
  eventRegistry.set(event, handler);
}

/**
 * Removes the event listener and it's handle from the current socket, and removes it from the mechanism of re registering the event in case of reconnect.
 * @param {string} event - Event that you want to remove to, e.g. appointment-open.
 * @param {callback function} handler - Callback handler when socket sends us this event.
 */
function socketOff(event, handler) {
  socket.off(event, handler);

  const currentHandler = eventRegistry.get(event);
  if (currentHandler === handler) {
    eventRegistry.delete(event);
  }
}

/**
 * Emits an event to the server socket. In case of the true reemitOnReconnect, re-emits the event in case of reconnect e.g. events that make the server socket join a room
 * @param {string} event - Event that you want to emit to, e.g. join-clinical.
 * @param {object} payload - Payload to be sent together with the event.
 * @param {{ reemitOnReconnect: boolean; }} [options={ reemitOnReconnect: false }] - Flag to add the event to the reemit registry
 */
function socketEmit(event, payload, options = { reemitOnReconnect: false }) {
  try {
    const safePayload = isBinary(payload) ? payload : cloneDeep(payload);
    JSON.stringify(safePayload);
    socket.emit(event, safePayload);

    if (options.reemitOnReconnect && autoRehydrate) {
      let alreadyInSet = false;
      for (const item of reconnectEmitRegistry) {
        if (item.event === event && isEqual(item.payload, safePayload)) {
          alreadyInSet = true;
          break;
        }
      }
      if (!alreadyInSet) {
        reconnectEmitRegistry.add({ event, payload: safePayload });
      }
    }
  } catch (error) {
    console.error(`Emit failed for event "${event}":`, error);
  }
}

/**
 * Cancels the re-emitting the event. This method needs to be used to remove the event in case
 * its no longer needed, e.g. socket leaves a room, you cancel the reemit for the event used to join the room.
 * @param {string} event - Event that you want to emit to, e.g. join-clinical.
 * @param {object} payload - Payload to be sent together with the event.
 */
function cancelReEmitFor(event, payload) {
  for (const entry of reconnectEmitRegistry) {
    if (entry.event === event && isEqual(entry.payload, payload)) {
      reconnectEmitRegistry.delete(entry);
      break;
    }
  }
}

/**
 * Method used to emit the rest of the events from the event registry of the socket, since we should wait for the
 * first event that initializes the socket and handles authentication to not have a false non-authenticated handles on the sever for the events
 * @param {string} event - Event that you want to cancel the re-emit to, e.g. join-clinical.
 * @param {object} payload - Payload to be sent together with the event.
 */
function emitRest() {
  if (pendingEmitAfterReconnect) {
    pendingEmitAfterReconnect = false;
    let isFirst = true;
    for (const { event, payload } of reconnectEmitRegistry) {
      if (isFirst) {
        isFirst = false;
        continue;
      }
      socketEmit(event, payload, { reemitOnReconnect: true });
    }
  }
}

/**
 * Function needed to restore the socket to it's former state before the reconnect
 */
function reinitializeAfterReconnect() {
  if (!autoRehydrate) return;

  eventRegistry.forEach((handler, event) => {
    socket.on(event, handler);
  });

  const iterator = reconnectEmitRegistry.values();
  const first = iterator.next().value;
  if (first) socketEmit(first.event, first.payload);
  if (reconnectEmitRegistry.size > 1) {
    pendingEmitAfterReconnect = true;
  }
}

/**
 * Function that disables the restoration of the socket state after a reconnect
 */
function setRehydration(enabled) {
  autoRehydrate = enabled;

  if (!enabled) {
    reconnectEmitRegistry.clear();
  }
}

/**
 * General function to initialize a socket connection, everything else gets build up from this part.
 */
function initSocket() {
  socket.connect();

  socket.on('connect', () => {
    if (!connectedOnce) {
      connectedOnce = true;
    } else {
      reinitializeAfterReconnect();
    }
  });

  socket.on('disconnect', (reason) => {
    console.warn('Socket disconnected:', reason);
  });

  socket.on('connect_error', (err) => {
    console.error('Connection error:', err);
  });
}

export {
  cancelReEmitFor,
  emitRest,
  initSocket,
  reinitializeAfterReconnect,
  setRehydration,
  socket,
  socketEmit,
  socketOff,
  socketOn
};
